import {
  Alert,
  Amount,
  Box,
  Button,
  CheckIcon,
  DatePicker,
  EditIcon,
  Link,
  Text,
  TextInput,
  useToast,
} from '@razorpay/blade/components';
import api from 'api';
import { getErrorsAsString } from 'components/Kyc/utils';
import useFetchCompensationDetails from 'components/PayDetails/hooks/useFetchCompensationDetails';
import { routePaths } from 'components/Routes/data';
import {
  FormWrapper,
  StepActionsWrapper,
  StepContentWrapper,
  StepHeader,
} from 'components/WizardViews/components';
import { format } from 'date-fns';
import { useFormWithProvider } from 'forms';
import { useSearchParams } from 'hooks/useSearchParams';
import React, { FormEvent, useState } from 'react';
import { useMutation } from 'react-query';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import loggedInUserSelectors from 'reducers/loggedInUser/selectors';
import { AppError } from 'utils/AppError';
import { convertToOrdinal } from 'utils/Dates';
import { useWizardInfo } from '../providers/WizardInfoProvider';
import { SummaryView, SummaryViewItem } from './components/SummaryView';
import { revisePaySteps, ReviseSalaryFormData, reviseSalarySchema } from './constants';
import {
  checkIsCurrentPayrollMonth,
  createUpdatePayPayload,
  getRevisionAlertMessageText,
  isDateAfterPayrollCycleStartDate,
} from './utils';
import { SalaryStructure } from 'components/SalaryStructure/types';
import useIsFeatureDisabledForRearch from 'access/useIsFeatureDisabledForRearch';
import { UserFilters } from 'api/gql-utils/payDetails/types';
import SalaryStructureDropDown from 'components/SalaryStructures/creation/components/SalaryStructureDropDown';

const SalaryDetailsStep = ({
  salaryStructureData,
}: {
  salaryStructureData?: SalaryStructure[];
}) => {
  const history = useHistory();
  const { updateStepStatus } = useWizardInfo();
  const toast = useToast();
  const loggedInUserId = useSelector(loggedInUserSelectors.peopleId) as number;
  const userId = useSearchParams().urlParams.get('userId') ?? loggedInUserId.toString();
  const { validate, register, formData, setFieldValue } =
    useFormWithProvider<ReviseSalaryFormData>();

  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  const payrollCycle = formData?.payrollSetupInfo?.payrollCycle!;

  const [isDateFieldDisabled, setIsDateFieldDisabled] = useState(true);

  // M3S1 feature flag check
  const { isM3S1Enabled } = useIsFeatureDisabledForRearch();

  const selectedEffectiveDate = formData?.effectiveFrom!;

  const payrollMonth = format(selectedEffectiveDate, 'yyyy-MM-dd');

  const currentCompensation = formData.currentAnnualCtc;

  let userFilters: UserFilters | undefined = undefined;
  if (currentCompensation === 0) {
    userFilters = {
      includeZeroSalaryUsers: true,
    };
  }

  const query = useFetchCompensationDetails({
    queryKey: ['getEmployeeCompensationData', userId, payrollMonth, formData.revisedCompensation],
    userId,
    payrollMonth,
    userFilters,
    userRevisions: [
      {
        revisionInfo: [
          {
            effectiveFromMonth: format(selectedEffectiveDate, 'yyyy-MM'),
            fullCtc: Number(formData.revisedCompensation!) * 100,
            proratedCtc: Number(formData.revisedCompensation!) * 100,
          },
        ],
      },
    ],
    enabled: false,
    onSuccess(data) {
      setFieldValue('ctcData', data.ctcBreakdownData);
      history.push({
        pathname: routePaths.payDetails.compensation.edit.review,
        search: `?userId=${userId}`,
      });
    },
    onError: (e: AppError) => {
      toast.show({
        content: getErrorsAsString(e),
        color: 'negative',
        duration: 3000,
      });
    },
  });

  // Filter salary structures based on debounced search value - only when M3S1 is enabled

  const payrollMonthMutation = useMutation({
    mutationFn: api.payrollQueries.getPayrollMonthFromDate,
  });

  const validateSalaryMutation = useMutation({
    mutationFn: api.compensationMutations.validatePay,
    onSuccess: () => query.refetch(),
    onError: (e: AppError) => {
      toast.show({
        content: getErrorsAsString(e),
        color: 'negative',
        duration: 3000,
      });
    },
  });

  const isLoading =
    query.isLoading || validateSalaryMutation.isLoading || payrollMonthMutation.isLoading;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    updateStepStatus(revisePaySteps.step1, 'success');
    if (validate(reviseSalarySchema)) {
      // Get effective payroll month from date
      const res = await payrollMonthMutation.mutateAsync({
        date: format(selectedEffectiveDate, 'yyyy-MM-dd'),
      });
      // Pass the effective payroll month in the validate api call.
      const effectivePayrollMonth = res.payrollGetMonthFromDate.data.payrollMonth;
      setFieldValue('effectivePayrollMonth', effectivePayrollMonth);

      validateSalaryMutation.mutate(
        createUpdatePayPayload({
          userId: userId!,
          annualSalary: parseInt(formData.revisedCompensation ?? '0'),
          effectiveFrom: effectivePayrollMonth,
        }),
      );
    }
  };

  const isCurrentPayrollMonth = checkIsCurrentPayrollMonth(selectedEffectiveDate);
  const isAfterPayrollCycleStartDate = isDateAfterPayrollCycleStartDate(
    selectedEffectiveDate,
    payrollCycle,
  );

  const handleEditDateClick = () => {
    setIsDateFieldDisabled(!isDateFieldDisabled);
    setIsDatePickerOpen(true);
  };

  const getSalaryStructureName = (salaryStructureId: string): string => {
    const salaryStructure = salaryStructureData?.find(
      (salaryStructure) => salaryStructure.id === salaryStructureId,
    );

    if (salaryStructure?.type === 'SALARY_STRUCTURE_TYPE_DEFAULT') {
      return 'Organisation Default';
    }

    return salaryStructure?.name || '';
  };

  return (
    <FormWrapper onSubmit={handleSubmit}>
      <StepContentWrapper maxWidth="600px">
        <SummaryView>
          <SummaryViewItem label="Current CTC">
            <Amount size="large" weight="medium" value={formData.currentAnnualCtc!} suffix="none" />
          </SummaryViewItem>
          <SummaryViewItem label="Monthly CTC">
            <Amount
              size="large"
              weight="medium"
              value={formData.currentAnnualCtc! / 12}
              suffix="none"
            />
          </SummaryViewItem>
          <SummaryViewItem label="Effective from">
            <Text size="large" weight="medium">
              {format(new Date(formData.currentCtcEffectiveFrom!), 'dd MMM, yyyy')}
            </Text>
          </SummaryViewItem>
          {isM3S1Enabled && formData.salaryStructureId?.[0] && (
            <SummaryViewItem label="Current template">
              <Link href={'/salary-structures'} size="large">
                {getSalaryStructureName(formData.salaryStructureId[0])}
              </Link>
            </SummaryViewItem>
          )}
        </SummaryView>

        <Box display="flex" flexDirection="column" gap="spacing.6" marginTop="spacing.8">
          <Box
            borderWidth="thin"
            borderColor="surface.border.gray.muted"
            borderRadius="medium"
            padding={'spacing.7'}>
            <StepHeader title="Revise" subtitle="Salary" />

            {isM3S1Enabled && (
              <SalaryStructureDropDown
                structures={salaryStructureData}
                registerProps={register('salaryStructureId', 'select')}
                hasAutoComplete={true}
              />
            )}
            <TextInput
              {...register('revisedCompensation')}
              isDisabled={isLoading}
              type="number"
              marginTop="spacing.5"
              label="Enter the revised compensation"
              prefix="₹"
              placeholder="Enter the revised compensation"
              size="medium"
              autoFocus
            />
          </Box>
          <Box
            borderWidth="thin"
            borderColor="surface.border.gray.muted"
            borderRadius="medium"
            padding={'spacing.7'}>
            <StepHeader title="Salary effective" subtitle="Date" />
            <Text as="p" size="medium" marginTop="spacing.5" color="surface.text.gray.muted">
              By default, the salary is effective from the{' '}
              <Text as="span" color="surface.text.gray.subtle">
                {`${convertToOrdinal(payrollCycle.startDate)} of every month`}
              </Text>
              . To make it effective from mid-month (e.g., 16th), specify the date.
            </Text>
            <Box display="flex" alignItems="center" marginTop="spacing.3" gap="spacing.4">
              <DatePicker
                label=""
                accessibilityLabel="Effective Date"
                {...register('effectiveFrom', 'date')}
                isDisabled={isDateFieldDisabled || isLoading}
                onOpenChange={({ isOpen }) => setIsDatePickerOpen(isOpen)}
                isOpen={isDatePickerOpen}
              />
              <Button
                isDisabled={isLoading}
                icon={EditIcon}
                variant="tertiary"
                onClick={handleEditDateClick}>
                {isDateFieldDisabled ? 'Edit' : 'Done'}
              </Button>
            </Box>
            {isAfterPayrollCycleStartDate && isCurrentPayrollMonth ? (
              <Alert
                marginTop="spacing.7"
                icon={CheckIcon}
                color="positive"
                description={getRevisionAlertMessageText(payrollCycle, selectedEffectiveDate)}
              />
            ) : null}
          </Box>
        </Box>
      </StepContentWrapper>
      <StepActionsWrapper maxWidth="600px">
        <span />
        <Button type="submit" isLoading={isLoading}>
          Next: Review
        </Button>
      </StepActionsWrapper>
    </FormWrapper>
  );
};

export default SalaryDetailsStep;
